import _, { reduce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { getAllergies as _getAllergies, getConditions as _getConditions, getDeals as _getDeals, getMedications as _getMedications, getProviderTypes as _getProviderTypes, getRecruitmentSources as _getRecruitmentSources, getRoutes as _getRoutes, getStudies as _getStudies, getSubjects as _getSubjects, getWorkflows as _getWorkflows } from '@/graphql/queries';
import { upsertSubject as _upsertSubject } from '@/graphql/mutations';
import { onUpsertTableChangelog } from '@/graphql/subscriptions';
import { isValidPhoneNumber } from 'react-phone-number-input';
import HeaderWithTabs from '@/HeaderWithTabs'
import ErrorModal from '@/ErrorModal';
import Breadcrumb from '@/Breadcrumb';
import Modal from '@/Modal';
import Privacy from '@/Privacy';
import ContactsList from '@/ContactsList';
import FilesList from '@/FilesList';
import {
	alcohol_use_frequencies, allergy_types, best_days_to_call, condition_statuses, diet_types,
	ethnicities, family_members, genders, languages, phone_types, physical_activity_levels,
	races, severities, smoker_statuses, states, stress_levels, titles
} from './enums';
import { checkboxField, dateField, phoneField, selectField, textAreaField, textField, timeField } from '@/commonFields';
import useFormFields from '@/hooks/useFormFields';
import RootContext from '@/RootContext';

const client = generateClient();

// A task.outcome may be null or "Done"
// FIXME: Consolidate SubjectEntry and SubjectWorkflow workflow component
const TASK_OUTCOMES = [
	{ id: null, label: 'To Do' },
	{ id: 'Done', label: 'Done' }
];

const _getSubjectsNoContacts = _getSubjects.replace(/subject_contacts\s*{[^}]*}/gs, '');

const getObject = ({ id }) => {
	return client.graphql({ query: _getSubjects, variables: { id, integrations: { clinicalConductor: true } } })
		.then(response => {
			const data = response.data.getSubjects.subjects?.[0];
			if (!data) throw new Error('Subject not found');
			// Response has all related data under "subjects" whereas this
			// form requires the related data under top-level keys.
			const flat = { subjects: {} };
			Object.keys(data).forEach(key => {
				if (key === 'subject_workflows') {
					const new_data = _.cloneDeep(data[key]);
					(new_data || []).forEach(sw => {
						try { sw.tasks_json = JSON.parse(sw.tasks_json); } catch (_) {}
						try { sw.workflow_tasks_json = JSON.parse(sw.workflow_tasks_json); } catch (_) {}
					});
					flat.subject_workflows = new_data;
				} else if (key.startsWith('subject_')) {
					if (data[key] && data[key].length > 0) flat[key] = data[key];
				} else {
					flat.subjects[key] = data[key];
				}
			});
			return flat;
		});
};

const upsertObject = ({ input, input_delete, input_integrations }) => {
	return client.graphql({ query: _upsertSubject, variables: { input, input_delete, input_integrations } })
		.then(response => {
			return response.data.upsertSubject;
		});
};

const CUSTOM_ALLERGY = {
	description: 'An allergy not from the list',
	id: null,
	name: 'Custom Allergy'
};

const getAllergies = ({ id } = {}) => {
	return client.graphql({ query: _getAllergies, variables: { ...(id ? { id } : undefined), sorting: { field: 'name', order: 'asc' } } })
		.then(response => {
			return [
				CUSTOM_ALLERGY,
				...response.data.getAllergies.allergies
			];
		});
};

const CUSTOM_CONDITION = {
	description: 'A condition not from the list',
	id: null,
	name: 'Custom Condition'
};

const getConditions = ({ id } = {}) => {
	return client.graphql({ query: _getConditions, variables: { ...(id ? { id } : undefined), sorting: { field: 'name', order: 'asc' } } })
		.then(response => {
			return [
				CUSTOM_CONDITION,
				...response.data.getConditions.conditions
			];
		});
};

const getDeals = ({ id } = {}) => {
	return client.graphql({ query: _getDeals, variables: { id, sorting: { field: 'deal_name', order: 'asc' } } })
		.then(response => {
			return response.data.getDeals.deals;
		});
};

const CUSTOM_MEDICATION = {
	description: 'A medicine not from the list',
	id: null,
	name: 'Custom Medication'
};

const getMedications = ({ id } = {}) => {
	return client.graphql({ query: _getMedications, variables: { ...(id ? { id } : undefined), sorting: { field: 'name', order: 'asc' } } })
		.then(response => {
			return [
				...response.data.getMedications.medications,
				CUSTOM_MEDICATION
			];
		});
};

const NULL_ROUTE = {
	description: null,
	id: null,
	name: 'Unspecified'
};

const getRoutes = ({ id } = {}) => {
	return client.graphql({ query: _getRoutes, variables: { ...(id ? { id } : undefined), sorting: { field: 'name', order: 'asc' } } })
		.then(response => {
			return [
				NULL_ROUTE,
				...response.data.getRoutes.routes
			];
		});
};

const getProviderTypes = ({ type } = {}) => {
	return client.graphql({ query: _getProviderTypes, variables: { ...(type ? { type } : undefined), sorting: { field: 'type', order: 'asc' } } })
		.then(response => {
			return [
				...response.data.getProviderTypes.provider_types
			];
		});
};

const getStudies = ({ id } = {}) => {
	return client.graphql({ query: _getStudies, variables: { id, sorting: { field: 'study_name', order: 'asc' } } })
		.then(response => {
			return response.data.getStudies.studies;
		});
};

const getRecruitmentSources = ({ id } = {}) => {
	return client.graphql({ query: _getRecruitmentSources, variables: { ...(id ? { id } : undefined), sorting: { field: 'source_name', order: 'asc' } } })
		.then(response => {
			return response.data.getRecruitmentSources.recruitment_sources;
		});
};

const getWorkflows = ({ id } = {}) => {
	return client.graphql({ query: _getWorkflows, variables: { id, sorting: { field: 'name', order: 'asc' } } })
		.then(response => {
			const data = response.data.getWorkflows.workflows;
			(data || []).forEach(w => {
				try { w.tasks_json = JSON.parse(w.tasks_json); } catch (_) {}
				try { w.workflow_tasks_json = JSON.parse(w.workflow_tasks_json); } catch (_) {}
			});
			return data;
		});
};

const calculate_bmi = ({ height_in, weight_lb }) => {
	if (!height_in || height_in === 0) return null;
	const bmi = (703 * weight_lb) / (height_in * height_in);
	// Round to 1 decimal place
	return Math.round(bmi * 10) / 10;
};

const blank_data = {
	subjects: {
		// All these null values will be "Prefer not to say"
		alcohol_use_frequency: null,
		diet_type: null,
		ethnicity: null,
		gender: null,
		physical_activity_level: null,
		preferred_language: null,
		race: null,
		smoker_status: null,
		stress_level: null
	},
	subject_phone_numbers: [{
	}]
};

export default ({ createLabel = 'Create', isFakeSave = false, isHeader = true, isLanding = false, links = [], onCancel, onSave, updateLabel = 'Save' }) => {
	const {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		jsonModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	} = useFormFields();

	const isConstructed = useRef(false);
	const selectOptions = useRef({});
	const setSelectOptionsByKey = key => value => {
		selectOptions.current[key] = value;
	};
	const location = useLocation();
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState();
	const [isLoaded, setIsLoaded] = useState(isCreate);
	const [isSaving, setIsSaving] = useState(false);
	const rootContext = useContext(RootContext);
	const auth = rootContext.get('auth');
	const [modal, setModal] = useState();
	const subscriptions = useRef([]);
	const [pushItems, setPushItems] = useState([]);

	const leftObj = _.cloneDeep(sanitizeObject(model));
	delete leftObj.subject_contacts;
	const rightObj = _.cloneDeep(origModel.current);
	delete rightObj.subject_contacts;
	const isEqual = _.isEqualWith(leftObj, rightObj);

	const [activeTab, _setActiveTab] = useState();
	const [contactsActivated, setContactsActivated] = useState(false);
	const [filesActivated, setFilesActivated] = useState(false);
	const setActiveTab = tab => {
		const queryParams = new URLSearchParams(location.search);
		queryParams.set('tab', tab);
		navigate({ search: `?${queryParams.toString()}` }, { replace: true });
		_setActiveTab(tab);
		if (tab === 'contacts') {
			setContactsActivated(true);
		} else if (tab === 'files') {
			setFilesActivated(true);
		}
	};

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setIsLoaded(true);
		setIsSaving(false);
	};

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (!isLoaded) {
			getObject({ id })
				.then(data => {
					origModel.current = data;
					// Make sure each array element has a __orig pointing to origModel
					const _data = _.cloneDeep(data);
					Object.keys(_data).forEach(key => {
						if (Array.isArray(_data[key])) {
							// Add __orig for each element to relate it back to origModel
							_data[key].forEach((e, i) => e.__orig = data[key][i]);
						}
					});
					setModel(_data);
				})
				.then(() => {
					setIsLoaded(true);
				})
				.catch(error => {
					origModel.current = _.cloneDeep(blank_data);
					// Make sure each array element has a __orig pointing to origModel
					const _data = _.cloneDeep(blank_data);
					setModel(_data);
					catchError(error);
				});
		} else {
			setModel(_.cloneDeep(blank_data));
		}
		const queryParams = new URLSearchParams(location.search);
		if (isCreate) {
			const recruitment_id = queryParams.get('recruitment_id');
			const study_id = queryParams.get('study_id');
			const deal_id = queryParams.get('deal_id');
			(async () => {
				// Retrieve the record from recruitment_sources and studies and preload a
				// subject_enrollment_status element.
				let recruitment_data;
				let study_data;
				if (recruitment_id) recruitment_data = await getRecruitmentSources({ id: recruitment_id });
				if (study_id) study_data = await getStudies({ id: study_id });
				if (deal_id) deal_data = await getDeals({ id: deal_id });
				if (recruitment_data || study_data) {
					const _data = {
						enrollment_status: 'Leads',
						...(recruitment_data && recruitment_data.length === 1 ? {
							recruitment_id,
							source_description: recruitment_data[0].source_description,
							source_name: recruitment_data[0].source_name,
						} : undefined),
						...(study_data && study_data.length === 1 ? {
							study_id,
							study_description: study_data[0].study_description,
							study_name: study_data[0].study_name
						} : undefined),
						...(deal_data && deal_data.length === 1 ? {
							deal_id,
							deal_description: deal_data[0].deal_description,
							deal_name: deal_data[0].deal_name
						} : undefined)
					};
					add_array({ key: 'subject_enrollment_status', data: _data });
				}
			})();
		} else {
			const tab = queryParams.get('tab');
			if (tab) {
				_setActiveTab(tab);
				if (tab === 'contacts') {
					setContactsActivated(true);
				}
			}
		}
		if (!isCreate) {
			subscriptions.current.push(client.graphql({	query: onUpsertTableChangelog, variables: {} }).subscribe({
				next: ({ data }) => {
					const items = data.onUpsertTableChangelog.table_changelog.filter(e => e.subject_id === id);
					if (items.length > 0) {
						setPushItems(prevState => [...prevState, ...items]);
					}
				},
				error: (error) => {
					console.error("Subscription error:", error);
				}
			}));
		}
	}

	useEffect(() => {
		const reduced_subject_contacts = {};
		pushItems.forEach(e => {
			if (e.table_name === 'subject_contacts' && e.subject_id === id) {
				if (!(e.table_id in reduced_subject_contacts)) {
					reduced_subject_contacts[e.table_id] = { contact_id: e.table_id };
				}
				Object.assign(reduced_subject_contacts[e.table_id], { [e.field_name]: e.new_value });
			}
		});
		Object.keys(reduced_subject_contacts).forEach(e => {
			const _data = reduced_subject_contacts[e];
			// Need this because subscription cannot useCallback to keep current
			const idx = (model.subject_contacts || []).findIndex(f => f.contact_id === _data.contact_id);
			if (idx !== -1) {
				// Update each field
				Object.keys(_data).forEach(field_name => {
					set_array({ key: 'subject_contacts', index: idx, name: field_name, value: _data[field_name] });
				});
			} else {
				// Add whole record
				add_array({ key: 'subject_contacts', data: _data, first: true });
			}
		});
		if (pushItems.length > 0) setPushItems(prevState => prevState.slice(pushItems.length));
	}, [pushItems]);

	useEffect(() => {
		return () => {
			subscriptions.current.forEach(e => e.unsubscribe());
		};
	}, []);

	const isDisabledOption = ({ other_key, others, option_key, value }) => option => {
		// null always allowed more than once
		if (option?.[option_key] === null) return false;
		// options should not allow the same option twice
		const disabled = option?.[option_key] !== value && !!(others || []).find(f => f[other_key] === option?.[option_key]);
		return disabled;
	};

	const details = useMemo(() => (
		<div className="row mt-5">
			<div className="col-3 mb-4">
				<div className="mb-3"><b><u>Personal Information</u></b></div>
				<div className="border shadow p-3 bg-form">
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Title', label1Key: 'id', label2Key: 'label', name: 'title', options: titles })}
					<div className="col-12" />
					{textField({ autoFocus: isCreate, get: get_value, getError, set: set_value, key: 'subjects', label: 'First Name', name: 'first_name' })}
					{textField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Middle Name', name: 'middle_name' })}
					{textField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Last Name', name: 'last_name' })}
					{dateField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Date of Birth', name: 'date_of_birth' })}
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Gender', name: 'gender', options: genders })}
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Preferred Language', name: 'preferred_language', options: languages })}
					{checkboxField({ className: 'col mb-1', get: get_value, getError, set: set_value, key: 'subjects', label: 'Opt-In to SMS messages', name: 'sms_opt_in' })}
					<a className="btn-link ms-4" onClick={() => setModal(<Modal onClose={() => setModal()} title=""><div style={{maxHeight: '80vh',overflowY: 'auto'}}><Privacy /></div></Modal>)} role="button">SMS Privacy Policy</a>
				</div>
			</div>
			<div className="col-3 mb-4">
				<div className="mb-3"><b><u>Demographics</u></b></div>
				<div className="border shadow p-3 bg-form">
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Ethnicity', name: 'ethnicity', options: ethnicities })}
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Race', name: 'race', options: races })}
				</div>
				<div className="mt-4 mb-3"><b><u>Lifestyle</u></b></div>
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Diet Type', name: 'diet_type', options: diet_types })}
						</div>
						<div className="col">
							{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Physical Activity Level', name: 'physical_activity_level', options: physical_activity_levels })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Stress Level', name: 'stress_level', options: stress_levels })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Sleep Hours', name: 'sleep_hours' })}
						</div>
					</div>
					{textAreaField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Lifestyle Notes', name: 'lifestyle_notes', rows: 3 })}
				</div>
			</div>
			<div className="col-3 mb-4">
				<div className="mb-3"><b><u>Health Information</u></b></div>
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{textField({ disabled: true, get: get_value, getError, set: set_value, key: 'subjects', label: 'BMI', name: 'bmi' })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Height (in)', name: 'height_in' })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Weight (lb)', name: 'weight_lb' })}
						</div>
					</div>
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Alcohol Use Frequency', name: 'alcohol_use_frequency', options: alcohol_use_frequencies })}
					{selectField({ get: get_value, getError, set: set_value, key: 'subjects', label: 'Smoker Status', name: 'smoker_status', options: smoker_statuses })}
				</div>
				<div className="mt-4 mb-3"><b><u>Study Enrollment(s)</u></b></div>
				{(model.subject_enrollment_status || []).map((e, i) => {
					const value = get_array({ key: 'subject_enrollment_status', index: i, name: 'study_id' });
					return (
						<div className="border shadow p-3 bg-form position-relative mb-4 pb-4" key={`${i}-${model.subject_enrollment_status.length}`}>
							{selectField({ disabled: !!e.ccid, defaultOptions: model.subject_enrollment_status ? model.subject_enrollment_status.map(e => ({ ...e, id: e.study_id })) : [], get: get_array, getData: getStudies, isOptionDisabled: isDisabledOption({ other_key: 'study_id', others: model.subject_enrollment_status, option_key: 'id', value }), options: selectOptions.current.studies, setOptions: setSelectOptionsByKey('studies'), getError, set: set_array, key: 'subject_enrollment_status', index: i, label: 'Study', label1Key: 'study_name', label2Key: 'study_description', name: 'study_id', idKey: 'study_id', valueKey: 'id' })}
							{selectField({ get: get_array, getError, set: set_array, key: 'subject_enrollment_status', index: i, label: 'Enrollment Status', name: 'enrollment_status', options: rootContext.get('enrollment_statuses'), valueKey: 'id' })}
							{model.subject_enrollment_status?.[i]?.recruitment_id &&
								selectField({ defaultOptions: model.subject_enrollment_status ? model.subject_enrollment_status.map(e => ({ ...e, id: e.recruitment_id })) : [], get: get_array, getData: getRecruitmentSources, getError, set: set_array, key: 'subject_enrollment_status', index: i, label: 'Source', label1Key: 'source_name', label2Key: 'source_description', name: 'recruitment_id', idKey: 'recruitment_id', valueKey: 'id' })
							}
							<a className="position-absolute" onClick={() => remove_array({ key: 'subject_enrollment_status', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
						</div>
					);
				})}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_enrollment_status', data: { enrollment_status: 'Leads' } })}>{(model.subject_enrollment_status || []).length === 0 ? 'Enroll in a Study' : 'Enroll in Another Study'}</button>
					</div>
				</div>
				<div className="mt-4 mb-3"><b><u>Recruitment Source(s)</u></b></div>
				{(model.subject_recruitment_sources || []).map((e, i) => {
					const value = get_array({ key: 'subject_recruitment_sources', index: i, name: 'study_id' });
					return (
						<div className="border shadow p-3 bg-form position-relative mb-4 pb-4" key={`${i}-${model.subject_recruitment_sources.length}`}>
							{selectField({ defaultOptions: model.subject_recruitment_sources ? model.subject_recruitment_sources.map(e => ({ ...e, id: e.recruitment_id })) : [], get: get_array, getData: getRecruitmentSources, getError, set: set_array, key: 'subject_recruitment_sources', index: i, label: 'Source', label1Key: 'source_name', label2Key: 'source_description', name: 'recruitment_id', idKey: 'recruitment_id', valueKey: 'id' })}
							<a className="position-absolute" onClick={() => remove_array({ key: 'subject_recruitment_sources', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
						</div>
					);
				})}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_recruitment_sources', data: {} })}>{(model.subject_recruitment_sources || []).length === 0 ? 'Add a Recruitment Source' : 'Add Another Recruitment Source'}</button>
					</div>
				</div>
				<div className="mt-4 mb-3"><b><u>Deal(s)</u></b></div>
				{(model.subject_deal_status || []).map((e, i) => {
					const value = get_array({ key: 'subject_deal_status', index: i, name: 'deal_id' });
					return (
						<div className="border shadow p-3 bg-form position-relative mb-4 pb-4" key={`${i}-${model.subject_deal_status.length}`}>
							{selectField({ disabled: !!e.ccid, defaultOptions: model.subject_deal_status ? model.subject_deal_status.map(e => ({ ...e, id: e.deal_id })) : [], get: get_array, getData: getDeals, isOptionDisabled: isDisabledOption({ other_key: 'deal_id', others: model.subject_deal_status, option_key: 'id', value }), options: selectOptions.current.deals, setOptions: setSelectOptionsByKey('deals'), getError, set: set_array, key: 'subject_deal_status', index: i, label: 'Deal', label1Key: 'deal_name', label2Key: 'deal_description', name: 'deal_id', idKey: 'deal_id', valueKey: 'id' })}
							{selectField({ get: get_array, getError, set: set_array, key: 'subject_deal_status', index: i, label: 'Status', name: 'status', options: rootContext.get('deal_statuses'), valueKey: 'id' })}
							<a className="position-absolute" onClick={() => remove_array({ key: 'subject_deal_status', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
						</div>
					);
				})}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_deal_status', data: { status: 'Leads' } })}>{(model.subject_deal_status || []).length === 0 ? 'Add a Deal' : 'Add Another Deal'}</button>
					</div>
				</div>
			</div>
			<div className="col-3 mb-4">
				{errors?.need_contact_info && <div className="mb-3" style={{color: 'var(--bs-form-invalid-color)'}}>{errors?.need_contact_info}</div>}
				<div className="mb-3"><b><u>Phone Numbers</u></b></div>
				{(model.subject_phone_numbers || []).map((e, i) => (
					<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_phone_numbers.length}`}>
						<div className="row">
							<div className="col-6">
								{phoneField({ get: get_array, getError, set: set_array, key: 'subject_phone_numbers', index: i, label: 'Phone Number', name: 'phone_number' })}
							</div>
							<div className="col-6">
								{selectField({ get: get_array, getError, set: set_array, key: 'subject_phone_numbers', index: i, label: 'Phone Type', name: 'phone_type', options: phone_types })}
							</div>
						</div>
						<div className="row">
							{selectField({ get: get_array, getError, set: set_array, key: 'subject_phone_numbers', index: i, isMulti: true, label: 'Best Day to Call', name: 'best_days_to_call', options: best_days_to_call, placeholder: 'Any day' })}
						</div>
						<div className="row mb-2">
							<div className="col-6">
								{timeField({ get: get_array, getError, set: set_array, key: 'subject_phone_numbers', index: i, label: 'Best Time to Call', name: 'best_time_to_call_start' })}
							</div>
							<div className="col-6">
								{timeField({ get: get_array, getError, set: set_array, key: 'subject_phone_numbers', index: i, label: 'End', name: 'best_time_to_call_end' })}
							</div>
						</div>
						<a className="position-absolute" onClick={() => remove_array({ key: 'subject_phone_numbers', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
					</div>
				))}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_phone_numbers', data: { best_days_to_call: null } })}>{(model.subject_phone_numbers || []).length === 0 ? 'Add a Phone Number' : 'Add Another Phone Number'}</button>
					</div>
				</div>
				<div className="mt-4 mb-3"><b><u>Email Addresses</u></b></div>
				{(model.subject_emails || []).map((e, i) => (
					<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_emails.length}`}>
						<div className="row mb-3">
							<div className="col">
								{textField({ get: get_array, getError, set: set_array, key: 'subject_emails', index: i, label: 'Email Address', name: 'email_address' })}
							</div>
						</div>
						<a className="position-absolute" onClick={() => remove_array({ key: 'subject_emails', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
					</div>
				))}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_emails', data: {} })}>{(model.subject_emails || []).length === 0 ? 'Add an Email Address' : 'Add Another Email Address'}</button>
					</div>
				</div>
				<div className="mt-4 mb-3"><b><u>Addresses</u></b></div>
				{(model.subject_addresses || []).map((e, i) => (
					<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_addresses.length}`}>
						<div className="row mb-3">
							<div className="col">
								{textField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'Address Line 1', name: 'address_line1' })}
								{textField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'Address Line 2', name: 'address_line2' })}
								<div className="row">
									<div className="col">
										{textField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'City', name: 'city' })}
									</div>
									<div className="col">
										{selectField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'State', label1Key: 'id', label2Key: 'label', name: 'state', options: states })}
									</div>
								</div>
								{textField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'Postal Code', name: 'postal_code' })}
								{textField({ get: get_array, getError, set: set_array, key: 'subject_addresses', index: i, label: 'Country', name: 'country' })}
							</div>
						</div>
						<a className="position-absolute" onClick={() => remove_array({ key: 'subject_addresses', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
					</div>
				))}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_addresses', data: {} })}>{(model.subject_addresses || []).length === 0 ? 'Add an Address' : 'Add Another Address'}</button>
					</div>
				</div>
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model), JSON.stringify(rootContext.get('enrollment_statuses'))]);

	const [route_count, set_route_count] = useState(0);
	const inc_route_count = () => set_route_count(prev => prev + 1);

	const set_condition = ({ key, index, name, option, value }) => {
		set_array({ key, index, name, value });
		if (name === 'condition_id' && value) {
			// We should reset the condition_name since we are using condition_id
			set_array({ key, index, name: 'condition_name', value: null });
		}
	};

	const medical = useMemo(() => (
		<>
			<div className="row mt-5">
				<div className="col">
					<div className="row">
						<div className="col">
							<div className="mb-3"><b><u>Providers</u></b></div>
							{(model.subject_physicians || []).map((e, i) => {
								return (
									<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_physicians.length}`}>
										<div className="row mb-3">
											<div className="col">
												<div className="row">
													<div className="col">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Physician Name', name: 'name' })}
													</div>
												</div>
												<div className="row">
													<div className="col">
														{selectField({ defaultOptions: model.subject_physicians ? [...model.subject_physicians.map(e => ({ type: e.provider_type }))] : [], options: selectOptions.current.provider_types, setOptions: setSelectOptionsByKey('provider_types'), get: get_array, getData: getProviderTypes, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Provider Type', label1Key: 'type', name: 'provider_type', idKey: 'provider_type', valueKey: 'type' })}
													</div>
													<div className="col">
														{dateField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Last Exam Date', name: 'last_exam_date' })}
													</div>
												</div>
												{textField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Address Line 1', name: 'address_line1' })}
												<div className="row">
													<div className="col">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Address Line 2', name: 'address_line2' })}
													</div>
													<div className="col">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'City', name: 'city' })}
													</div>
												</div>
												<div className="row">
													<div className="col">
														{selectField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'State', label1Key: 'id', label2Key: 'label', name: 'state', options: states })}
													</div>
													<div className="col">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Postal Code', name: 'postal_code' })}
													</div>
												</div>
												<div className="row">
													<div className="col">
														{phoneField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Main Phone', name: 'phone_number' })}
													</div>
													<div className="col">
														{phoneField({ get: get_array, getError, set: set_array, key: 'subject_physicians', index: i, label: 'Fax Number', name: 'fax_number' })}
													</div>
												</div>
											</div>
										</div>
										<a className="position-absolute" onClick={() => remove_array({ key: 'subject_physicians', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
									</div>
								);
							})}
							<div className="row mt-3">
								<div className="col-auto">
									<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_physicians', data: {} })}>{(model.subject_physicians || []).length === 0 ? 'Add a Provider' : 'Add Another Provider'}</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col">
					<div className="row">
						<div className="col">
							<div className="mb-3"><b><u>Medical History</u></b></div>
							{(model.subject_medical_history || []).map((e, i) => {
								const value = get_array({ key: 'subject_medical_history', index: i, name: 'condition_id' });
								return (
									<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_medical_history.length}`}>
										<div className="row mb-3">
											<div className="col">
												<div className="row">
													{selectField({ defaultOptions: model.subject_medical_history ? [CUSTOM_CONDITION, ...model.subject_medical_history.map(e => ({ description: e.condition_id_description, id: e.condition_id, name: e.condition_id_name }))] : [], get: get_array, getData: getConditions, isOptionDisabled: isDisabledOption({ other_key: 'condition_id', others: model.subject_medical_history, option_key: 'id', value }), options: selectOptions.current.conditions, setOptions: setSelectOptionsByKey('conditions'), getError, set: set_condition, key: 'subject_medical_history', index: i, label: 'Condition', label1Key: 'name', label2Key: 'description', name: 'condition_id', idKey: 'condition_id', valueKey: 'id' })}
												</div>
												{e.condition_id === null &&
													<div className="row">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_medical_history', index: i, label: 'Custom Condition Name', name: 'condition_name' })}
													</div>
												}
												{selectField({ get: get_array, getError, set: set_array, key: 'subject_medical_history', index: i, label: 'Condition Status', name: 'condition_status', options: condition_statuses })}
												{dateField({ get: get_array, getError, set: set_array, key: 'subject_medical_history', index: i, label: 'Diagnosed Date', name: 'diagnosed_date' })}
												{dateField({ get: get_array, getError, set: set_array, key: 'subject_medical_history', index: i, label: 'Resolved Date', name: 'resolved_date' })}
												{textField({ get: get_array, getError, set: set_array, key: 'subject_medical_history', index: i, label: 'Notes', name: 'notes' })}
											</div>
										</div>
										<a className="position-absolute" onClick={() => remove_array({ key: 'subject_medical_history', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
									</div>
								);
							})}
							<div className="row mt-3">
								<div className="col-auto">
									<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_medical_history', data: {} })}>{(model.subject_medical_history || []).length === 0 ? 'Add Medical History' : 'Add More Medical History'}</button>
								</div>
							</div>
						</div>
					</div>
					<div className="row mt-5">
						<div className="col">
							<div className="mb-3"><b><u>Family Medical History</u></b></div>
							{(model.subject_family_medical_history || []).map((e, i) => {
								const value = get_array({ key: 'subject_family_medical_history', index: i, name: 'condition_id' });
								return (
									<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_family_medical_history.length}`}>
										<div className="row mb-3">
											<div className="col">
												<div className="row">
													{selectField({ defaultOptions: model.subject_family_medical_history ? [CUSTOM_CONDITION, ...model.subject_family_medical_history.map(e => ({ description: e.condition_id_description, id: e.condition_id, name: e.condition_id_name }))] : [], get: get_array, getData: getConditions, isOptionDisabled: isDisabledOption({ other_key: 'condition_id', others: model.subject_family_medical_history, option_key: 'id', value }), options: selectOptions.current.conditions, setOptions: setSelectOptionsByKey('conditions'), getError, set: set_condition, key: 'subject_family_medical_history', index: i, label: 'Condition', label1Key: 'name', label2Key: 'description', name: 'condition_id', idKey: 'condition_id', valueKey: 'id' })}
												</div>
												{e.condition_id === null &&
													<div className="row">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_family_medical_history', index: i, label: 'Custom Condition Name', name: 'condition_name' })}
													</div>
												}
												{selectField({ get: get_array, getError, set: set_array, key: 'subject_family_medical_history', index: i, label: 'Family Member', name: 'family_member', options: family_members })}
												{textField({ get: get_array, getError, set: set_array, key: 'subject_family_medical_history', index: i, label: 'Notes', name: 'notes' })}
											</div>
										</div>
										<a className="position-absolute" onClick={() => remove_array({ key: 'subject_family_medical_history', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
									</div>
								);
							})}
							<div className="row mt-3">
								<div className="col-auto">
									<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_family_medical_history', data: {} })}>{(model.subject_family_medical_history || []).length === 0 ? 'Add Family Medical History' : 'Add More Family Medical History'}</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col">
					<div className="row">
						<div className="col">
							<div className="mb-3"><b><u>Allergies</u></b></div>
							{(model.subject_allergies || []).map((e, i) => {
								const value = get_array({ key: 'subject_allergies', index: i, name: 'allergy_id' });
								const set_allergy = ({ key, index, name, option, value }) => {
									set_array({ key, index, name, value });
									if (name === 'allergy_id' && value) {
										// We should reset the allergy_name since we are using allergy_id
										set_array({ key: 'subject_allergies', index: i, name: 'allergy_name', value: null });
									}
									};
								return (
									<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_allergies.length}`}>
										<div className="row mb-3">
											<div className="col">
												<div className="row">
													{selectField({ defaultOptions: model.subject_allergies ? [CUSTOM_ALLERGY, ...model.subject_allergies.map(e => ({ description: e.allergy_id_description, id: e.allergy_id, name: e.allergy_id_name }))] : [], get: get_array, getError, getData: getAllergies, isOptionDisabled: isDisabledOption({ other_key: 'allergy_id', others: model.subject_allergies, option_key: 'id', value }), options: selectOptions.current.allergies, setOptions: setSelectOptionsByKey('allergies'), getError, set: set_allergy, key: 'subject_allergies', index: i, label: 'Allergy', label1Key: 'name', label2Key: 'description', name: 'allergy_id', idKey: 'allergy_id', valueKey: 'id' })}
												</div>
												{e.allergy_id === null &&
													<div className="row">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_allergies', index: i, label: 'Custom Allergy Name', name: 'allergy_name' })}
													</div>
												}
												<div className="row">
													<div className="col">
														{selectField({ get: get_array, getError, set: set_array, key: 'subject_allergies', index: i, label: 'Allergy Type', name: 'allergy_type', options: allergy_types })}
													</div>
													<div className="col">
														{textField({ get: get_array, getError, set: set_array, key: 'subject_allergies', index: i, label: 'Reaction', name: 'reaction' })}
													</div>
													<div className="col">
														{selectField({ get: get_array, getError, set: set_array, key: 'subject_allergies', index: i, label: 'Severity', name: 'severity', options: severities })}
													</div>
												</div>
												{textField({ get: get_array, getError, set: set_array, key: 'subject_allergies', index: i, label: 'Notes', name: 'notes' })}
											</div>
										</div>
										<a className="position-absolute" onClick={() => remove_array({ key: 'subject_allergies', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
									</div>
								);
							})}
							<div className="row mt-3">
								<div className="col-auto">
									<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_allergies', data: { allergy_type: null, severity: null } })}>{(model.subject_allergies || []).length === 0 ? 'Add an Allergy' : 'Add Another Allergy'}</button>
								</div>
							</div>
						</div>
					</div>
					<div className="row mt-5">
						<div className="mb-3"><b><u>Medications</u></b></div>
						{(model.subject_medications || []).map((e, i) => {
							const medication_id = get_array({ key: 'subject_medications', index: i, name: 'medication_id' });
							const set_medication = ({ key, index, name, option, value }) => {
								set_array({ key, index, name, value });
								if (name === 'medication_id' && value) {
									// We should reset the medication_name since we are using medication_id
									set_array({ key: 'subject_medications', index: i, name: 'medication_name', value: null });
									// Set route to default_route
									set_array({ key: 'subject_medications', index: i, name: 'route_id', value: option.default_route_id });
									let route_name = option.default_route_id_name;
									if (option.default_route_id === null) {
										route_name = NULL_ROUTE.name;
									}
									set_array({ key: 'subject_medications', index: i, name: 'route_id_name', value: route_name });
									inc_route_count();
								}
								if (name === 'medication_id' && !value) {
									// Custom medication name
									// Set route to Unspecified
									set_array({ key: 'subject_medications', index: i, name: 'route_id', value: null });
									inc_route_count();
								}
							};
							return (
								<div className="col border shadow p-3 bg-form position-relative mb-4" key={`${i}-${model.subject_medications.length}`}>
									<div className="row mb-3">
										<div className="col">
											<div className="row">
												{selectField({ defaultOptions: model.subject_medications ? [CUSTOM_MEDICATION, ...model.subject_medications.map(e => ({ description: e.medication_id_description, id: e.medication_id, name: e.medication_id_name }))] : [], extraKeys: ['default_route_id', 'default_route_id_name'], get: get_array, getData: getMedications, isOptionDisabled: isDisabledOption({ other_key: 'medication_id', others: model.subject_medications, option_key: 'id', value: medication_id }), options: selectOptions.current.medications, setOptions: setSelectOptionsByKey('medications'), getError, set: set_medication, key: 'subject_medications', index: i, label: 'Medication', label1Key: 'name', label2Key: 'description', name: 'medication_id', idKey: 'medication_id', valueKey: 'id' })}
											</div>
											{e.medication_id === null &&
												<div className="row">
													{textField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'Custom Medication Name', name: 'medication_name' })}
												</div>
											}
											<div className="row">
												<div className="col">
													{textField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'Dosage And Frequency', name: 'dosage_and_frequency' })}
												</div>
												<div className="col" key={route_count}>
													{selectField({ defaultOptions: model.subject_medications ? [NULL_ROUTE, ...model.subject_medications.map(e => ({ description: null, id: e.route_id, name: e.route_id_name }))] : [], get: get_array, getData: getRoutes, options: selectOptions.current.routes, setOptions: setSelectOptionsByKey('routes'), getError, set: set_array, key: 'subject_medications', index: i, label: 'Route', label1Key: 'name', name: 'route_id', idKey: 'route_id', valueKey: 'id' })}
												</div>
											</div>
											<div className="row">
												<div className="col">
													{dateField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'Start Date', name: 'start_date' })}
												</div>
												<div className="col">
													{dateField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'End Date', name: 'end_date' })}
												</div>
											</div>
											{textField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'Prescribed by', name: 'prescribed_by' })}
											{textField({ get: get_array, getError, set: set_array, key: 'subject_medications', index: i, label: 'Notes', name: 'notes' })}
										</div>
									</div>
									<a className="position-absolute" onClick={() => remove_array({ key: 'subject_medications', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
								</div>
							);
						})}
						<div className="row mt-3">
							<div className="col-auto">
								<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_medications', data: { route_id: null, route_id_name: NULL_ROUTE.name } })}>{(model.subject_medications || []).length === 0 ? 'Add a Medication' : 'Add Another Medication'}</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const workflows = useMemo(() => {
		const get_task_outcome = ({ subject_workflow, task }) => () => {
			// Find the task in our tasks_json
			const t = (subject_workflow.tasks_json || {})[task.id];
			return t?.outcome || null;
		};
		const set_task_outcome = ({ subject_workflow, task }) => ({ index, value }) => {
			if (!subject_workflow.tasks_json) {
				// Initialize tasks_json. Will remove later if empty.
				subject_workflow.tasks_json = {};
			}
			// Find the task in our tasks_json
			const t = (subject_workflow.tasks_json || {})[task.id] || {};
			if (value) {
				t.outcome = value;
			} else {
				// Might as well delete key for null
				delete t.outcome;
			}
			if (Object.keys(t).length > 0) {
				subject_workflow.tasks_json[task.id] = t;
			} else {
				// Might as well delete key when empty
				delete subject_workflow.tasks_json[task.id];
			}
			if (Object.keys(subject_workflow.tasks_json).length === 0) {
				subject_workflow.tasks_json = null;
			}
			set_array({ key: 'subject_workflows', index, name: 'tasks_json', value: subject_workflow.tasks_json });
		};
		return (
			<>
				<div className="mt-5 mb-3"><b><u>Workflow(s)</u></b></div>
				{(model.subject_workflows || []).map((sw, i) => {
					const value = get_array({ key: 'subject_workflows', index: i, name: 'workflow_id' });
					const set_workflow = ({ key, index, name, option, value }) => {
						set_array({ key, index, name, value });
						if (name === 'workflow_id' && value) {
							console.log('option', option);
							// Init the subject_workflows.workflow_tasks_json
							set_array({ key, index, name: 'workflow_tasks_json', value: _.cloneDeep(option.tasks_json) });
							// Init purpose of subject_workflows.tasks_json -- This supports last_touchpoint & next_touchpoint
							const tasks_json = (option.tasks_json || []).reduce((prev, cur) => ({ ...prev, [cur.id]: { outcome: null } }), {});
							set_array({ key, index, name: 'tasks_json', value: tasks_json });
						}
					};
					const sw_tasks_json = (sw.workflow_tasks_json || []);
					return (
						<div className="row" key={i}>
							<div className="col col-lg-8">
								<div className="border shadow p-3 bg-form position-relative mb-4 pb-4" key={`${i}-${model.subject_workflows.length}`}>
									{selectField({ disabled: !!sw.id, defaultOptions: model.subject_workflows ? model.subject_workflows.map(e => ({ ...e, id: e.workflow_id, name: e.workflow_name })) : [], extraKeys: ['tasks_json'], get: get_array, getData: getWorkflows, isOptionDisabled: isDisabledOption({ other_key: 'workflow_id', others: model.subject_workflows, option_key: 'id', value }), options: selectOptions.current.workflows, setOptions: setSelectOptionsByKey('workflows'), getError, set: set_workflow, key: 'subject_workflows', index: i, label: 'Workflow', label1Key: 'name', name: 'workflow_id', idKey: 'workflow_id', valueKey: 'id' })}
									<div className="mt-4 mb-4"><b><u>Task(s)</u></b></div>
									<div className="row mb-3">
										<div className="col">
											<div className="">
												{sw_tasks_json.map((e, j) => (
													<div className="row" key={e.id}>
														<div className="col">
															<span className="d-inline-block mb-2"><b>{j + 1}.</b> {e.purpose}</span>
															{selectField({ className: 'col-auto', index: i, get: get_task_outcome({ subject_workflow: sw, task: e }), set: set_task_outcome({ subject_workflow: sw, task: e }), options: TASK_OUTCOMES })}
															{j < sw_tasks_json.length - 1 && <hr />}
														</div>
													</div>
												))}
											</div>
										</div>
									</div>
									<a className="position-absolute" onClick={() => remove_array({ key: 'subject_workflows', index: i })} role="button" style={{bottom: '0.5em', right: '1em'}}>Remove</a>
								</div>
							</div>
						</div>
					);
				})}
				<div className="row mt-3">
					<div className="col-auto">
						<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_workflows', data: {} })}>{(model.subject_workflows || []).length === 0 ? 'Add a Workflow' : 'Add Another Workflow'}</button>
					</div>
				</div>
			</>
		);
	}, [JSON.stringify(errors), JSON.stringify(model)]);

	const contacts = useMemo(() => contactsActivated ? (
		<>
			<div className="row mt-5 g-0">
				<div className="col">
					<ContactsList subject_id={id} />
				</div>
			</div>
		</>
	) : undefined, [contactsActivated, JSON.stringify(errors), JSON.stringify(model)]);

	const files = useMemo(() => filesActivated ? (
		<>
			<div className="row mt-5 g-0">
				<div className="col">
					<FilesList subject_id={id} />
				</div>
			</div>
		</>
	) : undefined, [filesActivated, JSON.stringify(errors), JSON.stringify(model)]);

	const notes = useMemo(() => (
		<div className="row mt-5">
			<div className="col">
				<div className="mb-3"><b><u>Notes</u></b></div>
				{(model.subject_notes || []).length > 0 ? (
					<div className="col border shadow p-3 bg-form mb-4">
						<div className="px-3" style={{maxHeight: '500px', overflowX: 'hidden', overflowY: 'auto'}}>
							{(model.subject_notes || []).map((e, i) => (
								<div className="row mb-4" key={`${i}-${model.subject_notes.length}`}>
									<div className="col position-relative">
										<span className="position-absolute" style={{right: '1em'}} title={moment(get_array({ key: 'subject_notes', index: i, name: 'created_at' })).format('LLL')}>{moment(get_array({ key: 'subject_notes', index: i, name: 'created_at' })).fromNow()}</span>
										{textAreaField({ get: get_array, getError, set: set_array, key: 'subject_notes', index: i, label: 'Note', name: 'note', rows: 5 })}
										<a className="col-auto px-0 position-absolute" onClick={() => remove_array({ key: 'subject_notes', index: i })} role="button" style={{bottom: '-0.5em', right: '1em'}}>Remove</a>
									</div>
								</div>
							))}
						</div>
						<div className="row mt-3">
							<div className="col-auto">
								<button className="btn btn-secondary ms-3" onClick={() => add_array({ key: 'subject_notes', data: {} })}>Add Another Note</button>
							</div>
						</div>
					</div>
				) : (
					<div className="row mt-3">
						<div className="col-auto">
							<button className="btn btn-secondary" onClick={() => add_array({ key: 'subject_notes', data: {} })}>Add a Note</button>
						</div>
					</div>
				)}
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const administration = useMemo(() => (
		<>
			<div className="row mt-5">
				<div className="col-3">
					<div className="mb-3"><b><u>Integrations</u></b></div>
					<div className="border shadow p-3 bg-form">
						{model.subjects?.ccid && textField({ disabled: true, get: get_value, getError, set: set_value, key: 'subjects', label: 'CCID', name: 'ccid' })}
						{textField({ inputClassName: 'text-center', disabled: true, get: get_value, getError, set: set_value, key: 'subjects', label: 'Lead Created At', name: 'lead_created_at', value: moment(model.subjects?.lead_created_at).format('M/D/YYYY h:mm:ss A') })}
					</div>
				</div>
			</div>
			<div className="row mt-5">
				<div className="col-3">
					<div className="mb-3"><b><u>Investment</u></b></div>
					<div className="border shadow p-3 bg-form">
						{checkboxField({ className: 'col mb-1', get: get_value, getError, set: set_value, key: 'subjects', label: 'Viewed active deal page', name: 'viewed_active_deal_page' })}
					</div>
				</div>
			</div>
		</>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	// Calculate BMI when height_in or weight_lb changes
	useEffect(() => {
		if (isLoaded) {
			const bmi = calculate_bmi({ height_in: model.subjects?.height_in, weight_lb: model.subjects?.weight_lb });
			// Use blank when the value is really whacky
			const _bmi = bmi === null || bmi > 100 ? <>&nbsp;</> : bmi;
			set_value({ key: 'subjects', name: 'bmi', value: _bmi });
		}
	}, [isLoaded, model.subjects?.height_in, model.subjects?.weight_lb]);

	const tabs = [
		{ name: 'details', label: 'Details', content: details },
		{ name: 'medical', label: 'Medical', content: medical },
		{ name: 'notes', label: 'Notes', content: notes },
		...(!isCreate ? [{ name: 'workflows', label: 'Workflows', content: workflows }] : []),
		...(!isCreate ? [{ name: 'contacts', label: 'Engagements', content: contacts }] : []),
		...(!isCreate ? [{ name: 'files', label: 'Files', content: files }] : []),
		...(auth?.groups?.includes('admin') ? [{ name: 'administration', label: 'Administration', content: administration }] : [])
	];

	const handleSave = () => {
		// Extract input from model
		const input = {
			..._.cloneDeep(changeModel.current),
			subjects: {
				..._.cloneDeep(changeModel.current.subjects),
				...(!isCreate ? { id } : undefined)
			}
		};
		console.log('model', model);
		const newErrors = {
			subjects: {},
			subject_phone_numbers: [],
			subject_emails: [],
			subject_enrollment_status: [],
			subject_allergies: [],
			subject_family_medical_history: [],
			subject_medical_history: [],
			subject_medications: [],
			subject_physicians: [],
			subject_workflows: []
		};
		(model.subject_phone_numbers || []).map((e, i) => {
			if (!e.phone_number || !isValidPhoneNumber(e.phone_number)) {
				newErrors.subject_phone_numbers[i] = { ...newErrors.subject_phone_numbers[i], phone_number: 'Must have valid Phone Number' };
			}
		});
		(model.subject_emails || []).map((e, i) => {
			if (!e.email_address) {
				newErrors.subject_emails[i] = { ...newErrors.subject_emails[i], email_address: 'Must have Email Address' };
			}
		});
		(model.subject_enrollment_status || []).map((e, i) => {
			/*
			if (!e.study_id) {
				newErrors.subject_enrollment_status[i] = { ...newErrors.subject_enrollment_status[i], study_id: 'Must have Study' };
			}
			*/
			if (!e.enrollment_status) {
				newErrors.subject_enrollment_status[i] = { ...newErrors.subject_enrollment_status[i], enrollment_status: 'Must have Enrollment Status' };
			}
		});
		(model.subject_allergies || []).map((e, i) => {
			if (e.allergy_id === undefined) {
				newErrors.subject_allergies[i] = { ...newErrors.subject_allergies[i], allergy_id: 'Must select Allergy' };
			} else if (e.allergy_id === null && !e.allergy_name) {
				newErrors.subject_allergies[i] = { ...newErrors.subject_allergies[i], allergy_name: 'Must have Custom Allergy Name' };
			}
		});
		(model.subject_family_medical_history || []).map((e, i) => {
			if (e.condition_id === undefined) {
				newErrors.subject_family_medical_history[i] = { ...newErrors.subject_family_medical_history[i], condition_id: 'Must select Condition' };
			} else if (e.condition_id === null && !e.condition_name) {
				newErrors.subject_family_medical_history[i] = { ...newErrors.subject_family_medical_history[i], condition_name: 'Must have Custom Condition Name' };
			}
		});
		(model.subject_medical_history || []).map((e, i) => {
			if (e.condition_id === undefined) {
				newErrors.subject_medical_history[i] = { ...newErrors.subject_medical_history[i], condition_id: 'Must select Condition' };
			} else if (e.condition_id === null && !e.condition_name) {
				newErrors.subject_medical_history[i] = { ...newErrors.subject_medical_history[i], condition_name: 'Must have Custom Condition Name' };
			}
		});
		(model.subject_medications || []).map((e, i) => {
			if (e.medication_id === undefined) {
				newErrors.subject_medications[i] = { ...newErrors.subject_medications[i], medication_id: 'Must select Medication' };
			} else if (!e.medication_id && !e.medication_name) {
				newErrors.subject_medications[i] = { ...newErrors.subject_medications[i], medication_name: 'Must have Custom Medication Name' };
			}
		});
		(model.subject_physicians || []).map((e, i) => {
			if (!e.name) {
				newErrors.subject_physicians[i] = { ...newErrors.subject_physicians[i], name: 'Must have Physician Name' };
			}
			// Not required but must be valid
			if (e.phone_number && !isValidPhoneNumber(e.phone_number)) {
				newErrors.subject_physicians[i] = { ...newErrors.subject_physicians[i], phone_number: 'Must have valid Phone Number' };
			}
			// Not required but must be valid
			if (e.fax_number && !isValidPhoneNumber(e.fax_number)) {
				newErrors.subject_physicians[i] = { ...newErrors.subject_physicians[i], fax_number: 'Must have valid Fax Number' };
			}
		});
		// First check to make sure there is a workflow_id set
		(model.subject_workflows || []).forEach((e, i) => {
			if (!e.workflow_id) {
				newErrors.subject_workflows[i] = { ...newErrors.subject_workflows[i], workflow_id: 'Must have Workflow' };
			}
		});
		// Now fixup input fields
		(input.subject_workflows || []).forEach(e => {
			if (e.tasks_json) e.tasks_json = JSON.stringify(e.tasks_json);
			// Remove unwanted read-only key
			delete e.workflow_tasks_json;
		});
		// Removing some unwanted work product
		Object.keys(input).forEach(parent => input[parent] = sanitizeObject(input[parent], '__local'));
		(input.subject_enrollment_status || []).forEach(e => {
			delete e.source_description;
			delete e.source_name;
			delete e.study_description;
			delete e.study_name;
		});
		(input.subject_medications || []).forEach(e => {
			delete e.route_id_name;
		});
		// bmi is calculated now, do not send it in
		delete input.subjects.bmi;
		// We add later but as read-only
		delete input.subject_contacts;
		if (
			!hasValue({ key: 'subject_phone_numbers' }) &&
			!hasValue({ key: 'subject_emails' })
		) {
			newErrors.need_contact_info = 'Must have at least one Phone Number or Email Address';
		}
		if (!hasValue({ key: 'subjects', name: 'first_name' })) {
			newErrors.subjects.first_name = 'First Name is required';
		}
		/*
		if (!hasValue({ key: 'subjects', name: 'last_name' })) {
			newErrors.subjects.last_name = 'Last Name is required';
		}
		*/
		/*
		if (!hasValue({ key: 'subjects', name: 'date_of_birth' })) {
			newErrors.subjects.date_of_birth = 'Date of Birth is required';
		}
		*/
		// Clean out empties
		Object.keys(newErrors).forEach(e => {
			if (
				(Array.isArray(newErrors[e]) && newErrors[e].length === 0) ||
				Object.keys(newErrors[e]).length === 0
			) {
				delete newErrors[e];
			}
		});
		console.log('newErrors', newErrors);
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}
		console.log('input', input);
		console.log('deleteModel', deleteModel.current);
		if (isFakeSave) {
			onSave && onSave({ data: model });
			return;
		}
		setIsSaving(true);
		upsertObject({
			input,
			...(deleteModel.current.length > 0 ? { input_delete: deleteModel.current } : undefined)
		})
			.then(response => {
				console.log('response', response);
				setIsSaving(false);
				onSave && onSave({ data: model });
			})
			.catch(catchError);
	};

	const handleCancel = () => onCancel && onCancel();

	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			{modal}
			<div className="container-fluid px-4 position-relative">
				<HeaderWithTabs
					isHeader={isHeader}
					isLoaded={isLoaded}
					onChange={setActiveTab}
					path="/app/customers/"
					tabs={tabs}
				>
					<>
						{!isLoaded ? (
							<div className="text-center" style={{marginTop:'5rem'}}>
								<button className="btn btn-primary" type="button" disabled>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Loading...
								</button>
							</div>
						) : (
							<>
								{links.length > 0 && <Breadcrumb className="mb-3 mx-3" links={links} />}
								<div className="row align-items-center position-absolute py-1 me-5" style={{right:0}}>
									<button className="col-auto ml-auto my-0 btn btn-primary" disabled={!((isCreate || !isEqual) && !isSaving)} onClick={handleSave}>{isCreate ? createLabel : updateLabel }</button>
									<a className="col-auto" role="button" onClick={handleCancel}>Cancel</a>
								</div>
								{isSaving && (
									<div
										className="position-fixed d-flex justify-content-center align-items-center"
										style={{inset: 0, zIndex: 1050}}
									>
										<button className="btn btn-primary" type="button" disabled>
											<span
												className="spinner-border spinner-border-sm me-2"
												role="status"
												aria-hidden="true"
											></span>
											Saving...
										</button>
									</div>
								)}
							</>
						)}
					</>
				</HeaderWithTabs>
			</div>
		</>
	);
};